/* eslint-disable camelcase */
import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PitchFxContainerWrapper } from '@gameday/components/PitchFx/PitchFx.style';
import PitchEffectsLayer from 'responsive-pitch-fx';
import promiseMany from 'promise-many/promise-many';
import imageLoader from 'image-loader/image-loader';
import FONTS from '@mlb/react-patterns/v4/style/fonts';
import { ballColors } from '../themes/base';
import CommercialBreakDataContainer from '../Ads/containers/CommercialBreakDataContainer';

import {
    StadiumWrapper,
    FieldWrapper,
    PitchFxWrapper
} from './PitchFx.style';

const GD5_OFFSET = 133;
export const defaultPitchFxConfig = {
    batterScaling: false,
    tiltShift: false,
    base_width: 1158,
    base_height: 869,
    font: FONTS.primaryFont,
    fontSize: 80,
    szBorder: 8,
    fadeOldPitches: true,
    viewBottomMargin: (186 + GD5_OFFSET),
    y_offset: (186 + (GD5_OFFSET / 5)), // trying different values to line up strike zone.
    batterTop: 27.8 + ((GD5_OFFSET / 869) * 100),
    hotColdZoneOutlierDefaultColor: 'rgba(221, 221, 221, .5)',
    hotColdZoneColorOpacity: 0.5,
    hotColdZoneDefaultColor: 'rgba(255, 255, 255, 1)',
    hotColdZoneGridColor: 'rgba(0, 0, 0, 1)',
    hotColdZoneBorderColor: 'rgba(0, 0, 0, 1)',
    hotColdZoneOutlierBorderColor: 'rgba(0, 0, 0, 0)',
    strikeZoneBorderColor: 'rgba(0, 0, 0, 1.0)',
    ...ballColors
};

PitchEffectsLayer.config(defaultPitchFxConfig);

const drawField = ({ payload, pitchLayerRef, setStadiumStyleData, forceDefaultStadium, defaultStadium, stadiumId }) => {
    if (payload?.stadium) {
        const stadiumImages = payload.getStadiumImages(forceDefaultStadium, defaultStadium);
        promiseMany([imageLoader.load(stadiumImages.bottom), imageLoader.load(stadiumImages.top)]).then(() => {
            const { top, bottom } = stadiumImages;
            return {
                top,
                bottom
            };
        }, () => {
            const { defaultTop, bottom } = stadiumImages;
            return {
                top: defaultTop,
                bottom
            };
        }).then((loadedImages) => {
            if (pitchLayerRef.current) {
                setStadiumStyleData(loadedImages);
            }
        });
    }
};

const drawAtBat = ({ pitchLayerRef, payload: { atbat, hotcoldzone, oldTimey, hidePitchFXData, hidePitchFXHover } }) => {
    if (pitchLayerRef.current) {
        const pitchLayer = pitchLayerRef.current;
        pitchLayer.config.oldTimey = !!oldTimey;
        pitchLayer.config.hoverData = !(hidePitchFXData === true || hidePitchFXHover === true);
        pitchLayer.draw(atbat, hotcoldzone);
    }
};

const PitchFx = ({
    batterModel,
    pitchFxConfig,
    viewType,
    forceDefaultStadium,
    defaultStadium,
    disableDynamicUniforms
}) => {
    const viewRef = useRef(null);
    const pitchLayerRef = useRef(null);

    const [stadiumStyleData, setStadiumStyleData] = useState(null);
    const fieldRef = useRef(null);
    const dayNight = batterModel?.dayNight || 'day';

    useEffect(() => {
        const field = fieldRef.current;

        if (pitchLayerRef.current === null) {
            const pxl = new PitchEffectsLayer(pitchFxConfig);
            pxl.anchor(field);
            pitchLayerRef.current = pxl;
        }
        return () => {
            pitchLayerRef.current.destroy();
            pitchLayerRef.current = null;
        };
    }, [batterModel?.gamePk]);

    useEffect(() => {
        drawField({
            payload: batterModel,
            pitchLayerRef,
            setStadiumStyleData,
            forceDefaultStadium,
            defaultStadium
        });
    }, [batterModel?.stadium, dayNight, forceDefaultStadium, defaultStadium]);

    useEffect(() => {
        try {
            drawAtBat({
                payload: batterModel,
                pitchLayerRef
            });
        } catch (err) {
            console.error('pfx render', err);
        }
    }, [batterModel?.atbat?.pitches, batterModel?.atbat?.uniformData]);

    return (
        <PitchFxContainerWrapper ref={viewRef}>
            <CommercialBreakDataContainer viewType={viewType} adId={'midInning-video'} type={'video'} />
            <CommercialBreakDataContainer viewType={viewType} adId={'midInning'} type={'banner'} />
            <PitchFxWrapper className="pitch_fx" id="pitch_fx">
                <StadiumWrapper className={`stadium ${dayNight}`}>
                    <FieldWrapper className={'field responsive-pitch-fx'} stadiumImages={stadiumStyleData} ref={fieldRef}></FieldWrapper>
                </StadiumWrapper>
            </PitchFxWrapper>
        </PitchFxContainerWrapper>
    );
};

PitchFx.propTypes = {
    isLoading: PropTypes.bool,
    batterModel: PropTypes.object,
    pitchFxConfig: PropTypes.object,
    viewType: PropTypes.string,
    forceDefaultStadium: PropTypes.bool,
    defaultStadium: PropTypes.string,
    currentUniforms: PropTypes.object,
    disableDynamicUniforms: PropTypes.bool
};

export default memo(PitchFx);
